import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  EventEmitter,
  Input,
  OnInit,
  Output,
  TemplateRef,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormBuilder,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { IonicBaseComponent } from '@front-end/core/ionic-hooks';

export interface MacroCategory {
  id?: number;
  name?: string;
  items: SubItem[];
}

export interface SubItem {
  id?: number;
  name?: string;
  code?: string;
  itemCount?: number;
  totalBudget?: number;
  residualBudget?: number;
  usedBudget?: number;
  articles?: ArticleItem[];
}

export interface ArticleItem {
  id?: number;
  name?: string;
  code?: string;
}

@Component({
  selector: 'ui-article-select-list',
  templateUrl: './article-select-list.component.html',
  styleUrls: ['./article-select-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: ArticleSelectListComponent,
    },
  ],
})
export class ArticleSelectListComponent
  extends IonicBaseComponent
  implements ControlValueAccessor, OnInit
{
  currentView: 'categories' | 'items' | 'articles' = 'categories';

  @Input() categories: MacroCategory[] = [];
  @Input() showAmounts = true;
  @Input() showFilters = true;
  @Input() filterProperty = 'totalBudget';
  @Input() filtersConfig: { key: string; label: string }[] = [];

  @Input() allowMultipleSelection = false;

  // Opens straight on the articles of this item, skipping the categories and
  // items steps (used when the budget item was already picked elsewhere).
  @Input() initialCategory?: MacroCategory;
  @Input() initialItem?: SubItem;

  @ContentChild('articleTemplate') articleTemplate?: TemplateRef<unknown>;
  @ContentChild('itemTemplate') itemTemplate?: TemplateRef<unknown>;
  @ContentChild('categoryTemplate') categoryTemplate?: TemplateRef<unknown>;

  selectedCategory?: MacroCategory;
  selectedItem?: SubItem;
  selectedArticle?: ArticleItem;

  currentItems: SubItem[] = [];
  currentArticles: ArticleItem[] = [];

  @Output() categorySelected = new EventEmitter<MacroCategory>();
  @Output() itemSelected = new EventEmitter<SubItem>();
  @Output() articleSelected = new EventEmitter<ArticleItem>();

  readonly articleFormControl = this.formBuilder.control<ArticleItem | null>(
    null
  );

  /* eslint-disable @typescript-eslint/no-empty-function */
  private onChange: (value: ArticleItem | null) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(
    private formBuilder: FormBuilder,
    private changeDetector: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit(): void {
    if (!this.initialCategory || !this.initialItem) {
      return;
    }

    this.selectedCategory = this.initialCategory;
    this.currentItems = this.initialCategory.items || [];
    this.selectedItem = this.initialItem;
    this.currentArticles = this.initialItem.articles || [];
    this.currentView = 'articles';
  }

  selectCategory(category: MacroCategory): void {
    this.selectedCategory = category;
    this.currentItems = category.items || [];
    this.currentView = 'items';
    this.categorySelected.emit(category);
    this.changeDetector.markForCheck();
  }

  selectItem(item: SubItem): void {
    this.selectedItem = item;
    this.currentArticles = item.articles || [];
    this.currentView = 'articles';
    this.itemSelected.emit(item);
    this.changeDetector.markForCheck();
  }

  selectArticle(article: ArticleItem): void {
    if (this.isArticleSelected(article)) {
      this.selectedArticle = undefined;
      this.articleFormControl.setValue(null);
      this.onChange(null);
    } else {
      this.selectedArticle = article;
      this.articleFormControl.setValue(article);
      this.onChange(article);
    }

    this.articleSelected.emit(this.selectedArticle);
    this.changeDetector.markForCheck();
  }

  isArticleSelected(article: ArticleItem): boolean {
    return this.selectedArticle?.id === article.id;
  }

  goBack(): void {
    if (this.currentView === 'articles') {
      this.currentView = 'items';
      this.changeDetector.markForCheck();
      return;
    }
    if (this.currentView === 'items') {
      this.currentView = 'categories';
      this.changeDetector.markForCheck();
    }
  }

  filterFor(property: string): void {
    if (property === this.filterProperty) {
      this.filterProperty = '';
      this.changeDetector.markForCheck();
      return;
    }
    this.filterProperty = property;
    this.changeDetector.markForCheck();
  }

  getBudgetAmount(): number {
    if (!this.selectedItem) return 0;

    if (this.filterProperty === 'totalBudget') {
      return this.selectedItem.totalBudget || 0;
    }
    if (this.filterProperty === 'residualBudget') {
      return this.selectedItem.residualBudget || 0;
    }
    if (this.filterProperty === 'usedBudget') {
      return this.selectedItem.usedBudget || 0;
    }
    return 0;
  }

  writeValue(article: ArticleItem): void {
    this.selectedArticle = article;
    this.articleFormControl.setValue(article);
    this.changeDetector.markForCheck();
  }

  registerOnChange(fn: (value: ArticleItem | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.articleFormControl.disable();
      return;
    }
    this.articleFormControl.enable();
  }
}
